import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useProfile } from "./auth";

/**
 * Intraday quotes for whatever the page is currently showing.
 *
 * Two rules shape everything here.
 *
 * **Only what is visible.** The ranking table renders all ~215 rows at once
 * with no virtualization, so "visible" is a viewport question rather than a
 * slice of the array. An IntersectionObserver answers it, and the result is
 * that quota scales with attention instead of with universe size — a person
 * looking at fifteen rows costs fifteen quotes, not two hundred.
 *
 * **A quote is never load-bearing.** Every price on the page already has a
 * real value, the snapshot close. A failed poll leaves that in place and says
 * nothing, so nothing here throws, retries hard, or surfaces an error state.
 * That is also why this does not use the `call()` client in vportfolio.ts:
 * that one is built to fail loudly, which is right for money and wrong here.
 */

const QuotesResponse = z.object({
  quotes: z.record(
    z.string(),
    z.object({ price: z.number(), observedAt: z.string() }).nullable(),
  ),
  asOfDate: z.string(),
});

export interface LiveQuote {
  price: number;
  observedAt: string;
}

/** Matches the endpoint's own ceiling; asking for more is a 400. */
const MAX_SYMBOLS = 40;

const POLL_MS = 30_000;

/** Coalesces a scroll into one request rather than one per row crossing. */
const SETTLE_MS = 300;

/**
 * Tracks which symbols are on screen.
 *
 * One shared observer rather than one per row, and the visible set is only
 * published after the viewport has settled — a fast scroll through the whole
 * table would otherwise fire a request for every row it passed.
 *
 * `register` is stable, and callers must keep their own ref callback stable
 * too (see `CandidateRow`). A ref whose identity changes each render makes
 * React detach and reattach it every time, which here means unobserve →
 * observe → the observer fires → state changes → render → repeat. That is an
 * infinite loop, not a slow one, and it hard-hangs the tab.
 */
export function useVisibleSymbols(): {
  register: (symbol: string, el: Element | null) => void;
  visible: readonly string[];
} {
  const [visible, setVisible] = useState<readonly string[]>([]);
  const onScreen = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const bySymbol = useRef(new Map<Element, string>());

  const publish = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const next = [...onScreen.current].sort();
      setVisible((prev) =>
        prev.length === next.length && prev.every((s, i) => s === next[i]) ? prev : next,
      );
    }, SETTLE_MS);
  }, []);

  /**
   * Built on first use rather than in an effect.
   *
   * Refs attach before passive effects run, so an effect-built observer does
   * not exist yet when the first rows register — and with stable refs there is
   * no second attachment to catch it, so nothing would ever be observed.
   */
  const getObserver = useCallback((): IntersectionObserver | null => {
    if (observer.current) return observer.current;
    // jsdom has no IntersectionObserver, and neither do very old browsers.
    // Quoting nothing is a clean degradation: the closes still render.
    if (typeof IntersectionObserver === "undefined") return null;

    observer.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const symbol = bySymbol.current.get(entry.target);
          if (!symbol) continue;
          if (entry.isIntersecting) onScreen.current.add(symbol);
          else onScreen.current.delete(symbol);
        }
        publish();
      },
      // A little margin so a row is quoted just before it is read.
      { rootMargin: "120px" },
    );
    return observer.current;
  }, [publish]);

  useEffect(
    () => () => {
      observer.current?.disconnect();
      observer.current = null;
      bySymbol.current.clear();
      onScreen.current.clear();
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const register = useCallback(
    (symbol: string, el: Element | null) => {
      const obs = getObserver();
      if (!obs) return;
      if (el) {
        bySymbol.current.set(el, symbol);
        obs.observe(el);
      } else {
        // React calls the ref with null on unmount; the element is gone, so the
        // symbol goes with it or the visible set would only ever grow as the
        // filters narrow.
        for (const [node, sym] of bySymbol.current) {
          if (sym === symbol) {
            obs.unobserve(node);
            bySymbol.current.delete(node);
          }
        }
        onScreen.current.delete(symbol);
        publish();
      }
    },
    [getObserver, publish],
  );

  return { register, visible };
}

/**
 * Polls `/api/quotes` for the given symbols.
 *
 * Signed-out callers get an empty map and no request at all: `/api/*` requires
 * the `authenticated` role, so an anonymous visitor simply keeps seeing the
 * closes. That is the designed degradation, not a failure.
 */
export function useQuotes(symbols: readonly string[]): {
  quotes: ReadonlyMap<string, LiveQuote>;
  enabled: boolean;
} {
  const { profile } = useProfile();
  const [quotes, setQuotes] = useState<ReadonlyMap<string, LiveQuote>>(new Map());

  // The identity of `symbols` changes on every render; its contents do not.
  const key = useMemo(() => [...symbols].sort().join(","), [symbols]);
  const enabled = Boolean(profile) && key.length > 0;

  useEffect(() => {
    if (!enabled) {
      setQuotes(new Map());
      return;
    }

    let cancelled = false;
    const wanted = key.split(",").slice(0, MAX_SYMBOLS);

    const poll = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const res = await fetch(`/api/quotes?symbols=${encodeURIComponent(wanted.join(","))}`);
        if (!res.ok) return;
        const body = QuotesResponse.parse(await res.json());
        if (cancelled) return;

        setQuotes((prev) => {
          // Merged rather than replaced: scrolling back to a row that has left
          // the request set should not blank a price it already had.
          const next = new Map(prev);
          for (const [symbol, quote] of Object.entries(body.quotes)) {
            if (quote) next.set(symbol, quote);
          }
          return next;
        });
      } catch {
        // A missed poll is not an event. The close is still on screen.
      }
    };

    void poll();
    const id = setInterval(() => void poll(), POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, key]);

  return { quotes, enabled };
}
